import { useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { AUTH_SESSION_QUERY_KEY, fetchAuthSession } from "@/features/auth/api/auth-session-client";
import {
  auth,
  clearSsoRedirectPending,
  getRedirectResult,
  hasSsoRedirectPending,
  signOut,
  subscribeToAuthState,
} from "@/features/auth/api/identity-platform-auth";
import { mapAuthErrorToMessage } from "@/features/auth/model/auth-error-messages";
import { getAuthErrorMessage } from "@/features/auth/model/auth-errors";
import { AuthorizationFailure } from "@/features/auth/ui/authorization-failure";
import { LoginForm } from "@/features/auth/ui/login-form";
import { ApiError } from "@/shared/api/api-error";
import { Callout } from "@/shared/ui/callout";
import { LoadingState } from "@/shared/ui/loading-state";

function LoginHeader() {
  return (
    <Stack spacing={1.25}>
      <Typography variant="overline" color="primary.main">
        Situate Deployment Console
      </Typography>
      <Typography variant="h5">Sign in to Situate</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
        Access operational intelligence for your assigned deployments.
      </Typography>
    </Stack>
  );
}

function getContinuePath(search: string): string {
  const continuePath = new URLSearchParams(search).get("continue");

  if (!continuePath || !continuePath.startsWith("/") || continuePath.startsWith("//")) {
    return "/deployments";
  }

  return continuePath;
}

export function LoginRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const continuePath = getContinuePath(location.search);
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ssoIncomplete, setSsoIncomplete] = useState(false);
  const [authorizationFailure, setAuthorizationFailure] = useState<string | null>(null);

  useEffect(() => {
    let isSubscribed = true;
    let authStateReady = false;
    let redirectResultReady = false;
    let bootstrapStarted = false;
    const ssoWasAttempted = hasSsoRedirectPending();

    async function clearFailedAuthState() {
      if (!auth.currentUser) {
        return;
      }

      try {
        await signOut();
      } catch {
        // The original auth/session error is more important to surface.
      }
    }

    async function bootstrapSignedInSession() {
      if (bootstrapStarted) {
        return;
      }

      if (!authStateReady || !redirectResultReady) {
        return;
      }

      if (!auth.currentUser) {
        if (ssoWasAttempted) {
          setSsoIncomplete(true);
        }
        setChecking(false);
        return;
      }

      bootstrapStarted = true;

      try {
        const authSession = await fetchAuthSession();

        if (!isSubscribed) {
          return;
        }

        queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, authSession);
        navigate(continuePath, { replace: true });
      } catch (sessionError) {
        if (!isSubscribed) {
          return;
        }

        if (sessionError instanceof ApiError && sessionError.statusCode === 403) {
          setAuthorizationFailure(getAuthErrorMessage(sessionError.code));
          setChecking(false);
          return;
        }

        await clearFailedAuthState();

        if (!isSubscribed) {
          return;
        }

        setError(mapAuthErrorToMessage(sessionError));
        setChecking(false);
      }
    }

    getRedirectResult(auth)
      .then(() => {
        if (!isSubscribed) {
          return;
        }

        clearSsoRedirectPending();
        redirectResultReady = true;
        void bootstrapSignedInSession();
      })
      .catch(async (redirectError: unknown) => {
        if (!isSubscribed) {
          return;
        }

        clearSsoRedirectPending();

        if (
          import.meta.env.DEV &&
          import.meta.env.MODE !== "test" &&
          redirectError instanceof Error
        ) {
          const errorCode = (redirectError as { code?: string }).code;
          console.debug("[Auth] SSO redirect failed", {
            name: redirectError.name,
            code: errorCode,
          });
        }

        await clearFailedAuthState();

        if (!isSubscribed) {
          return;
        }

        setError(mapAuthErrorToMessage(redirectError));
        redirectResultReady = true;
        setChecking(false);
      });

    const unsubscribe = subscribeToAuthState(() => {
      if (!isSubscribed) {
        return;
      }

      authStateReady = true;
      void bootstrapSignedInSession();
    });

    return () => {
      isSubscribed = false;
      unsubscribe();
    };
  }, [continuePath, navigate, queryClient]);

  if (checking) {
    return <LoadingState message="Checking authentication..." />;
  }

  if (authorizationFailure) {
    return (
      <AuthorizationFailure
        message={authorizationFailure}
        onReturnToLogin={() => {
          setAuthorizationFailure(null);
          setError(null);
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 6,
      }}
    >
      <Card
        sx={(theme) => ({
          width: "100%",
          maxWidth: 420,
          bgcolor: alpha(theme.palette.background.paper, 0.82),
          borderColor: alpha(theme.palette.common.white, 0.12),
          backdropFilter: "blur(18px)",
        })}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3}>
            <LoginHeader />
            {ssoIncomplete && !error && (
              <Callout severity="info">
                Sign-in was not completed. If you cancelled or something went wrong, please try
                again.
              </Callout>
            )}
            {error && <Callout severity="error">{error}</Callout>}
            <LoginForm />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
